import React, { createContext, useContext, useState, useCallback } from "react";

const defaultPermissions = {
  CanRead: false,
  CanView: false,
  CanCopy: false,
  CanDelete: false,
  CanEdit: false,
  CanCreate: false,
};

const userInfo = localStorage.user_info
  ? {
      email: "",
      name: "",
      role: "",
      module: "",
      permissions: defaultPermissions,
      ...JSON.parse(localStorage.user_info),
    }
  : {
      email: "",
      name: "",
      role: "",
      module: "",
      permissions: defaultPermissions,
    };

const initialAuthData = localStorage.auth_data
  ? JSON.parse(localStorage.auth_data)
  : {
      homeAccountId: "",
      environment: "",
      tenantId: "",
      username: "",
      localAccountId: "",
      name: "",
      authorityType: "",
      idToken: "",
      accessToken: "",
    };

// Define error type
type ErrorState = {
  isError: boolean;
  message: string;
};

type AppContextType = {
  appData: {
    isFormChanged: boolean;
    showUnSavedModal: boolean;
    url: string;
  };
  userData: typeof userInfo;
  authData: typeof initialAuthData;
  error: ErrorState;
  // @ts-ignore
  updateAppData: (newData: Partial<typeof appData>) => void;
  updateUserData: (newData: Partial<typeof userInfo>) => void;
  updateAuthData: (newData: Partial<typeof initialAuthData>) => void;
  updateError: (newError: Partial<ErrorState>) => void;
  userInitialized: boolean;
  setUserInitialized: (value: boolean) => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appData, setAppData] = useState({
    isFormChanged: false,
    showUnSavedModal: false,
    url: "",
  });

  const [userData, setUserData] = useState(userInfo);
  const [authData, setAuthData] = useState(initialAuthData);
  const [error, setError] = useState<ErrorState>({
    isError: false,
    message: "",
  });

  const [userInitialized, setUserInitialized] = useState(false);
  const [selectedLandscape, setSelectedLandscape] = useState<any>(null);

  const updateAppData = useCallback((newData: Partial<typeof appData>) => {
    setAppData((prev) => ({ ...prev, ...newData }));
  }, []);

  const updateUserData = useCallback(
    (newData: Partial<typeof userInfo>) => {
      setUserData((prev: any) => {
        const updated = { ...prev, ...newData };
        localStorage.setItem("user_info", JSON.stringify(updated));
        return updated;
      });
      // const updated = { ...userData, ...newData };
      // setUserData(updated);
      // localStorage.setItem('user_info', JSON.stringify(updated));
    },
    [userData]
  );

  const updateAuthData = useCallback(
    (newData: Partial<typeof initialAuthData>) => {
      setAuthData((prev: any) => {
        const updated = { ...prev, ...newData };
        localStorage.setItem("auth_data", JSON.stringify(updated));
        return updated;
      });
      // const updated = { ...authData, ...newData };
      // setAuthData(updated);
      // localStorage.setItem('auth_data', JSON.stringify(updated));
    },
    [authData]
  );

  const updateError = useCallback((newError: Partial<ErrorState>) => {
    setError((prev) => ({ ...prev, ...newError }));
  }, []);

  return (
    <AppContext.Provider
      value={{
        appData,
        userData,
        authData,
        error,
        userInitialized,
        setUserInitialized,
        updateAppData,
        updateUserData,
        updateAuthData,
        updateError,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
